/**
 * Legal Verification Panel Component
 * Displays verification results for legal answers including accuracy check, warnings, and corrections
 */

import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore,
  VerifiedUser,
  Warning,
  Error,
  CheckCircle,
  Info,
  Security,
} from '@mui/icons-material';

export interface VerificationResult {
  isAccurate: boolean;
  confidence: 'high' | 'medium' | 'low';
  confidenceScore?: number; // 0-100 optional percentage
  warnings: string[];
  corrections: string[];
  verifiedBy: string;
  verificationTimestamp: string;
}

export interface LegalVerificationPanelProps {
  verificationResult?: VerificationResult;
  isVerifying?: boolean;
  expanded?: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const getConfidenceColor = (confidence: string) => {
  switch (confidence) {
    case 'high':
      return 'success';
    case 'medium':
      return 'warning';
    case 'low':
      return 'error';
    default:
      return 'info';
  }
};

const getConfidenceLabel = (confidence: string) => {
  switch (confidence) {
    case 'high':
      return 'Vysoká spolehlivost';
    case 'medium':
      return 'Střední spolehlivost';
    case 'low':
      return 'Nízká spolehlivost';
    default:
      return 'Neověřeno';
  }
};

const getConfidencePercent = (confidence: string, explicit?: number) => {
  if (typeof explicit === 'number' && !Number.isNaN(explicit)) {
    return Math.max(0, Math.min(100, Math.round(explicit)));
  }
  switch (confidence) {
    case 'high':
      return 85;
    case 'medium':
      return 60;
    case 'low':
      return 35;
    default:
      return 0;
  }
};

const LegalVerificationPanel: React.FC<LegalVerificationPanelProps> = ({
  verificationResult,
  isVerifying = false,
  expanded = false,
  onExpandChange,
}) => {
  const handleAccordionChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
    onExpandChange?.(isExpanded);
  };

  if (!verificationResult && !isVerifying) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <Accordion expanded={expanded} onChange={handleAccordionChange}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            bgcolor: verificationResult?.isAccurate ? 'success.light' : 'warning.light',
            color: verificationResult?.isAccurate ? 'success.contrastText' : 'warning.contrastText',
            '&:hover': {
              bgcolor: verificationResult?.isAccurate ? 'success.main' : 'warning.main',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Security sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Verifikace odpovědi
            </Typography>
            
            {isVerifying && (
              <Box sx={{ ml: 1, minWidth: 100 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                  Ověřuji...
                </Typography>
                <LinearProgress />
              </Box>
            )}
            
            {verificationResult && (
              <Chip
                label={`${getConfidenceLabel(verificationResult.confidence)} (${getConfidencePercent(verificationResult.confidence, verificationResult.confidenceScore)}%)`}
                size="small"
                color={getConfidenceColor(verificationResult.confidence) as any}
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 3 }}>
          {isVerifying && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography color="text.secondary" gutterBottom>
                Probíhá automatická verifikace odpovědi...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kontroluji faktickou správnost, úplnost a možná rizika
              </Typography>
            </Box>
          )}

          {verificationResult && (
            <Box>
              {/* Accuracy Status */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {verificationResult.isAccurate ? (
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                  ) : (
                    <Error color="error" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="h6">
                    {verificationResult.isAccurate ? 'Odpověď prošla verifikací' : 'Zjištěny nesrovnalosti'}
                  </Typography>
                </Box>
                
                <Alert 
                  severity={verificationResult.isAccurate ? 'success' : 'warning'} 
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    {verificationResult.isAccurate 
                      ? 'Automatická kontrola neidentifikovala zásadní chyby v právních informacích.'
                      : 'Automatická kontrola identifikovala možné nepřesnosti. Doporučujeme konzultaci s právníkem.'
                    }
                  </Typography>
                </Alert>
              </Box>

              {/* Warnings */}
              {verificationResult.warnings.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Warning color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="warning.main">
                      Upozornění ({verificationResult.warnings.length})
                    </Typography>
                  </Box>
                  
                  <List dense>
                    {verificationResult.warnings.map((warning, index) => (
                      <ListItem key={index} sx={{ pl: 0 }}>
                        <ListItemIcon>
                          <Info color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {warning}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Corrections */}
              {verificationResult.corrections.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Error color="error" sx={{ mr: 1 }} />
                    <Typography variant="h6" color="error.main">
                      Doporučené opravy ({verificationResult.corrections.length})
                    </Typography>
                  </Box>
                  
                  <List dense>
                    {verificationResult.corrections.map((correction, index) => (
                      <ListItem key={index} sx={{ pl: 0 }}>
                        <ListItemIcon>
                          <Error color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {correction}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Verification Info */}
              <Box sx={{ 
                bgcolor: 'grey.50', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <strong>Verifikováno:</strong> {verificationResult.verifiedBy}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  <strong>Čas verifikace:</strong> {new Date(verificationResult.verificationTimestamp).toLocaleString('cs-CZ')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  <strong>Poznámka:</strong> Automatická verifikace slouží jako pomocný nástroj. 
                  Pro konkrétní právní záležitosti vždy konzultujte kvalifikovaného advokáta.
                </Typography>
              </Box>

              {/* Verification Score Visualization */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Celkové hodnocení spolehlivosti:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinearProgress
                    variant="determinate"
                    value={getConfidencePercent(verificationResult.confidence, verificationResult.confidenceScore)}
                    color={getConfidenceColor(verificationResult.confidence) as any}
                    sx={{ flexGrow: 1, mr: 2, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" sx={{ minWidth: 40 }}>
                    {`${getConfidencePercent(verificationResult.confidence, verificationResult.confidenceScore)}%`}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default LegalVerificationPanel;
